import { NgTemplateOutlet } from "@angular/common";
import {
  Component,
  computed,
  EventEmitter,
  HostListener,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
} from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { Language } from "../../../constants/enums";
import { PeriodModelType, SelectedPeriodModelType } from "../../../models";
import { LanguageService, PeriodApiService } from "../../../services";
import { ItemSliderComponent } from "../../dashboard/item-slider/item-slider";
import { PeriodRailComponent, PeriodRailStep } from "../period-rail/period-rail";
import { CHART_FREQUENCY_CYCLES, ChartFrequencyOptionType, PeriodSlotType } from "./utils";

@Component({
  selector: "adaa-period-slider-variant",
  imports: [NgTemplateOutlet, TranslatePipe, ItemSliderComponent, PeriodRailComponent],
  templateUrl: "./period-slider-variant.html",
  styleUrl: "./period-slider-variant.scss",
})
export class PeriodSliderVariantComponent implements OnInit {
  @Input() public set _startDate(date: number | undefined) {
    this.startDate.set(date);
  }
  @Input() public set _endDate(date: number | undefined) {
    this.endDate.set(date);
  }
  @Input() public set _frequency(freq: number) {
    this.frequency.set(freq);
  }
  @Input() public set _stepRange(stepRange: number) {
    this.stepRange.set(stepRange);
  }

  @Output() selectedChartFrequencyCycle = new EventEmitter<string>();
  @Output() selectPeriod = new EventEmitter<Pick<SelectedPeriodModelType, "period">>();

  private readonly _languageService = inject(LanguageService);
  private readonly _periodApiService = inject(PeriodApiService);

  emitOnInit = input<boolean>(true);
  activePeriod = input<Pick<SelectedPeriodModelType, "period">>();
  periodId = input<number | undefined>(undefined);
  showChartCycle = input<boolean>(true);
  kpiType = input<number>(0);
  isEKPI = input<boolean>(false);
  showMonthsAsNumbers = input<boolean>(false);
  showMonths = input<boolean>(true);
  boldYears = input<boolean>(false);
  truncateStart = signal<boolean>(false);
  truncateEnd = signal<boolean>(false);
  stepRange = signal<number>(6);
  isReady = signal<boolean>(false);
  startDate = signal<number | undefined>(undefined);
  endDate = signal<number | undefined>(undefined);
  frequency = signal<number>(Constants.FREQUENCY.MONTHLY);
  disableSlider = signal<boolean>(false);
  periodList = signal<PeriodModelType[]>([]);
  actualPeriod = signal<PeriodModelType | undefined>(undefined);
  chartFrequencyCycles = signal<Pick<ChartFrequencyOptionType, "id" | "label">[]>([]);
  activeChartFrequencyCycle = signal<string>(Constants.CHART_FREQUENCY_OPTION.ONE_YEAR);
  periodSlots = signal<PeriodSlotType[]>([]);
  railSteps = signal<PeriodRailStep[]>([]);

  selectedPeriod = computed(() => {
    const period = this.activePeriod()?.period ?? this.actualPeriod();
    if (!period) return undefined;

    return this._resolveSlotId(period);
  });

  readonly isActive = (id: string) => this.activeChartFrequencyCycle() === id;

  readonly #STEPS_OFFSET = 1;
  isMobile = window.innerWidth < 992;

  @HostListener("window:resize")
  public onResize() {
    this.isMobile = window.innerWidth < 992;
  }

  public ngOnInit() {
    this._getChartFrequencyCycles();
    this.initPeriods(true);
  }

  public recalibrate(
    data: Partial<{
      startDate: number;
      endDate: number;
      frequency: number;
      stepRange: number;
    }>
  ) {
    if (data.startDate) this.startDate.set(data.startDate);
    if (data.endDate) this.endDate.set(data.endDate);
    if (data.frequency) this.frequency.set(data.frequency);
    if (data.stepRange) this.stepRange.set(data.stepRange);

    this.initPeriods(false);
  }

  public selectChartCycle(id: string) {
    this.activeChartFrequencyCycle.set(id);
    this.selectedChartFrequencyCycle.emit(id);
    this._calculateSteps(this.activePeriod()?.period ?? this.actualPeriod()!, this.periodSlots());
  }

  public periodChanged($event: number, recalculateSteps = true) {
    const steps = this.railSteps();
    const period = this.periodList().find(({ id }) => id === $event);
    const stepIndex = steps.findIndex(({ id }) => id === $event);

    if (!period) return;

    this.selectPeriod.emit({ period });

    const lastStepIndex = steps.length - 1;
    const canLeftRecalculate = stepIndex <= this.#STEPS_OFFSET;
    const canRightRecalculate = stepIndex >= lastStepIndex - this.#STEPS_OFFSET;

    if (recalculateSteps && (canLeftRecalculate || canRightRecalculate)) {
      this._calculateSteps(period, this.periodSlots());
    }
  }

  public initPeriods(onInit: boolean) {
    this._periodApiService.getActualPeriod({ useLoader: true }).subscribe({
      next: (res) => this.actualPeriod.set(res.responseData),
      complete: () => this._getDateInterval(onInit),
    });
  }

  private _getChartFrequencyCycles() {
    const data = {
      kpiType: this.kpiType(),
      frequency: this.frequency(),
    };

    const list = CHART_FREQUENCY_CYCLES.filter(({ visibleIf }) => visibleIf(data));
    switch (list.length) {
      case 0: {
        this.disableSlider.set(true);
        return;
      }
      case 1: {
        this.selectChartCycle(list[0].id);
        break;
      }
      default: {
        this.selectChartCycle(list[list.length - 1].id);
        break;
      }
    }

    this.chartFrequencyCycles.set([...list]);
  }

  private _getDateInterval(onInit: boolean) {
    const currentPlan = AdaaHelper.plan;
    const input = {
      startTS: this.startDate() ?? currentPlan?.startYear,
      endTS: this.endDate() ?? currentPlan?.endYear,
      frequency: this.frequency(),
      useLoader: true,
    };

    this._periodApiService.getDateRange(input).subscribe({
      next: (res) => this.periodList.set([...res.responseData]),
      complete: () => {
        this._generateSlots();

        let period: PeriodModelType | undefined;
        if (onInit && this.periodId()) {
          period = this.periodList().find(({ id }) => id === this.periodId());
        }
        if (!period) {
          period = this.activePeriod()?.period ?? this.actualPeriod()!;
        }

        this._calculateSteps(period, this.periodSlots());
        this.isReady.set(true);
        if (this.emitOnInit()) this.periodChanged(this._resolveSlotId(period), false);
      },
    });
  }

  private _generateSlots() {
    switch (this.frequency()) {
      case Constants.FREQUENCY.ANNUAL: {
        this._generateAnnualSlots();
        break;
      }

      case Constants.FREQUENCY.QUARTERLY: {
        this._generateQuarterlySlots();
        break;
      }

      case Constants.FREQUENCY.SEMIANNUAL: {
        this._generateSemiAnnualSlots();
        break;
      }

      default: {
        this._generateMonthlySlots();
        break;
      }
    }
  }

  private _generateAnnualSlots() {
    const slots: PeriodSlotType[] = this.periodList()
      .filter(({ month }) => month === 12)
      .map((period) => {
        const locale = this._languageService.current() === Language.Arabic ? "ar-AE" : "en-GB";
        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          timeZone: Constants.uaeTimezoneName,
        };

        if (this.showMonths()) {
          options.month = this.showMonthsAsNumbers() ? "2-digit" : "short";
        }

        const df = new Intl.DateTimeFormat(locale, options);

        return {
          periodId: period.id,
          label: df.format(AdaaHelper.getDubaiTime(period.date)),
          shortLabel: String(period.year),
          year: period.year,
          month: period.month,
          quarter: period.quarter,
          semester: period.semester,
        };
      });

    this.periodSlots.set([...slots]);
  }

  private _generateSemiAnnualSlots() {
    const slots: PeriodSlotType[] = this.periodList()
      .filter(({ month }) => month === 12 || month === 6)
      .map((period) => {
        const locale = this._languageService.current() === Language.Arabic ? "ar-AE" : "en-GB";
        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "2-digit",
          timeZone: Constants.uaeTimezoneName,
        };

        const df = new Intl.DateTimeFormat(locale, options);

        return {
          periodId: period.id,
          label: df.format(AdaaHelper.getDubaiTime(period.date)),
          shortLabel: `S${period.semester}`,
          year: period.year,
          month: period.month,
          quarter: period.quarter,
          semester: period.semester,
        };
      });

    this.periodSlots.set([...slots]);
  }

  private _generateQuarterlySlots() {
    const slots: PeriodSlotType[] = this.periodList()
      .filter(({ month }) => month === 12 || month === 9 || month === 6 || month === 3)
      .map((period) => {
        const locale = this._languageService.current() === Language.Arabic ? "ar-AE" : "en-GB";
        const options: Intl.DateTimeFormatOptions = {
          year: "numeric",
          month: "2-digit",
          timeZone: Constants.uaeTimezoneName,
        };

        const df = new Intl.DateTimeFormat(locale, options);

        return {
          periodId: period.id,
          label: df.format(AdaaHelper.getDubaiTime(period.date)),
          shortLabel: `Q${period.quarter}`,
          year: period.year,
          month: period.month,
          quarter: period.quarter,
          semester: period.semester,
        };
      });

    this.periodSlots.set([...slots]);
  }

  private _generateMonthlySlots() {
    const slots: PeriodSlotType[] = this.periodList().map((period) => {
      const locale = this._languageService.current() === Language.Arabic ? "ar-AE" : "en-GB";
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        timeZone: Constants.uaeTimezoneName,
      };

      if (this.showMonths()) {
        options.month = this.showMonthsAsNumbers() ? "2-digit" : "short";
      }

      const df = new Intl.DateTimeFormat(locale, options);
      const monthFormat = new Intl.DateTimeFormat(locale, {
        month: this.showMonthsAsNumbers() ? "2-digit" : "short",
        timeZone: Constants.uaeTimezoneName,
      });

      return {
        periodId: period.id,
        label: df.format(AdaaHelper.getDubaiTime(period.date)),
        shortLabel: monthFormat.format(AdaaHelper.getDubaiTime(period.date)),
        year: period.year,
        month: period.month,
        quarter: period.quarter,
        semester: period.semester,
      };
    });

    this.periodSlots.set([...slots]);
  }

  private _calculateSteps(activePeriod: PeriodModelType, slots: PeriodSlotType[]) {
    const totalSlots = this.stepRange() * 2;

    if (this.activeChartFrequencyCycle() === Constants.CHART_FREQUENCY_OPTION.ONE_YEAR)
      slots = slots.filter((slot) => slot.year === activePeriod.year);
    if (this.activeChartFrequencyCycle() === Constants.CHART_FREQUENCY_OPTION.TWO_YEAR) {
      const lastPeriod = slots[slots.length - 1];
      const isLastPeriod = activePeriod.year === lastPeriod.year;
      slots = isLastPeriod
        ? slots.filter((slot) => slot.year === activePeriod.year || slot.year === activePeriod.year - 1)
        : slots.filter((slot) => slot.year === activePeriod.year || slot.year === activePeriod.year + 1);
    }

    if (slots.length <= totalSlots) {
      this._updateTruncatedEdges(0, slots.length - 1, slots.length);
      this.railSteps.set(slots.map((slot) => this._toRailStep(slot)));
      return;
    }

    const index = this._findSlotIndex(slots, activePeriod);

    const stepRange = this.stepRange();
    const firstSlot = slots[0];
    const lastSlot = slots[slots.length - 1];

    const lowerLimitIndex = Math.max(index - stepRange, 1);
    const upperLimitIndex = Math.min(index + stepRange, slots.length - 2);

    this._updateTruncatedEdges(lowerLimitIndex, upperLimitIndex, slots.length);
    const middleSlots = slots.slice(lowerLimitIndex, upperLimitIndex + 1);

    const combinedSteps = [firstSlot, ...middleSlots, lastSlot].map((slot) => this._toRailStep(slot));
    const railSteps = Array.from(new Map(combinedSteps.map((step) => [step.id, step])).values());

    this.railSteps.set(railSteps);
  }

  /**
   * @description
   *  A slot stands for the whole range it closes — Q3 is September, H2 December — while
   *  the actual period is a plain month, so it carries an id no slot holds. Left raw it
   *  highlights nothing, and `periodChanged()` drops it for good: the id is not in
   *  `periodList()` either, so the initial period never reaches the parent.
   */
  private _resolveSlotId(period: PeriodModelType): number {
    const slots = this.periodSlots();
    if (slots.length === 0) return period.id;

    return slots[this._findSlotIndex(slots, period)].periodId;
  }

  /**
   * @description
   *  The slot a period belongs to is the first one closing at or after it — August falls
   *  in Q3, which closes in September. A period past the last slot belongs to that one.
   */
  private _findSlotIndex(slots: PeriodSlotType[], period: PeriodModelType): number {
    const index = slots.findIndex(({ year, month }) => {
      if (year !== period.year) return year > period.year;
      return month >= period.month;
    });

    if (index === -1) return slots.length - 1;
    return index;
  }

  private _toRailStep(slot: PeriodSlotType): PeriodRailStep {
    return { id: slot.periodId, label: slot.shortLabel, year: slot.year };
  }

  private _updateTruncatedEdges(lowerLimitIndex: number, upperLimitIndex: number, slotsLength: number): void {
    const isSecondMiddleCloseToFirst = lowerLimitIndex <= 1;
    const isLastMiddleCloseToLast = upperLimitIndex >= slotsLength - 2;

    this.truncateStart.set(!isSecondMiddleCloseToFirst);
    this.truncateEnd.set(!isLastMiddleCloseToLast);
  }
}
